
   // Non-blocking replacement for alert(): a blocking dialog freezes the whole
   // tab's event loop, including the Threedium WebGL iframe's own runtime —
   // after dismissing the alert, the iframe stopped responding to input and
   // customers had to refresh the page to retry. This toast never blocks it.
   //
   // Scoped to `popup` (see findThrediumContext() below) rather than a page-wide
   // id — there's more than one place Threedium's widget can appear on a page.
   function showThrediumPopupToast(popup, message) {
    var toast = popup && popup.querySelector('.threedium-popup-toast');
    if (!toast) {
      console.warn('[ATC3dium] toast element missing, message was:', message);
      return;
    }
    toast.textContent = message;
    toast.classList.add('active');
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(function () {
      toast.classList.remove('active');
    }, 4000);
   }

   // The entire "ADD PERSONALISATION" panel (Initials/Position/Size/Font/Colour)
   // lives inside Threedium's cross-origin iframe — we have no DOM access to it,
   // so we can't just clear the Initials boxes ourselves. After a blocked submit
   // those boxes were left holding the rejected value, and typing into an already
   // non-empty maxlength box does nothing until the customer selects-all first.
   // Reloading just the iframe (not the whole page) resets it to a clean, typeable
   // state — nothing was actually saved on a blocked submit, so there's nothing to
   // lose besides re-picking Position/Size/Font/Colour if they'd already set those.
   function resetThrediumIframe(popup) {
    var iframe = popup && popup.querySelector('.threedium-iframe');
    if (!iframe || !iframe.dataset.src) return;
    var src = iframe.dataset.src;
    iframe.src = 'about:blank';
    setTimeout(function () {
      iframe.src = src;
    }, 50);
   }

   // There isn't just one place Threedium's widget can appear — the Initials-only
   // popup, the "TRY IN 3D" popup, the dedicated full-page customiser, and an
   // inline "Product Customiser" section on some product templates have each
   // turned out to use different markup (found one at a time, the hard way).
   // Rather than keep hardcoding a new id/class every time another one turns up,
   // find it generically: prefer whichever popup is actually open (has .active);
   // otherwise, whatever `.threedium-popup-toast` exists on the page — inline/
   // full-page integrations aren't popups, so they don't toggle .active at all —
   // walk up to the nearest ancestor carrying the data-*-error attributes. Any
   // current or future Threedium integration works automatically as long as it
   // follows the same contract: a container with data-blank-initials-error /
   // data-invalid-initials-error, a `.threedium-popup-toast` child, and its
   // iframe tagged `.threedium-iframe` with `dataset.src` set.
   function findThrediumContext() {
    var activePopup = document.querySelector('.threedium-popup.active');
    if (activePopup) return activePopup;

    var toast = document.querySelector('.threedium-popup-toast');
    return toast ? toast.closest('[data-blank-initials-error]') : null;
   }

   // Threedium's customAttributes use raw keys like "Personalisation_Text" —
   // Shopify prints property keys verbatim on Checkout, the order-status page,
   // and order confirmation emails (none of which run our theme's Liquid), so
   // whatever we send here is exactly what the customer/back-office sees there.
   // Rename to clean labels at the source, matching the wording already shown
   // in Threedium's own "ADD PERSONALISATION" panel.
   const PERSONALISATION_PREFIX = /^Personalisation_/i;
   const PERSONALISATION_KEY_LABELS = {
    Personalisation_Text: 'Initials',
    Personalisation_Position: 'Position',
    Personalisation_Size: 'Size',
    Personalisation_Font: 'Font',
    Personalisation_Color: 'Colour'
   };

   window.addEventListener('message', function(event) {
    if (!event.origin.includes('threedium.co.uk') && !event.origin.includes('globe-trotter.threedium.co.uk')) return;

    const data = event.data;

    if (data && data.type === 'NYLA_ADD_TO_CART' && Array.isArray(data.data) && data.data.length > 0) {

      // Define the ID mapping (old Threedium key → real Shopify variant ID)
      const idMapping = {
        37333922283670: 42786596946115,
        // THUNDERBALL · Carry-On - 4 Wheels | Navy/Navy/Black: Threedium sends a
        // stale variant key for this product's own line (confirmed via the
        // /cart/add.js payload — NOT the Initialling tracker, which is fine).
        54684123234689: 55793406673281
        // Add more mappings here if needed, e.g.:
        // 12345678901234: 99999999999999,
      };

      // Get current selected variant ID (fallback/override if needed)
      let currentVariantId = null;
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.selectedVariantId) {
        currentVariantId = window.ShopifyAnalytics.meta.selectedVariantId;
      }

      const items = data.data.map((item, index) => {
        // Start with the key from Threedium
        let variantId = item.key;

        // Apply mapping if the key matches one of the old IDs
        if (idMapping.hasOwnProperty(variantId)) {
          variantId = idMapping[variantId];
          console.log(`Mapped old ID ${item.key} → new ID ${variantId}`);
        }

        // Optional: If length-based override still needed (you can remove if mapping covers everything)
        // if (currentVariantId !== null) {
        //   if (data.data.length === 1 && index === 0) {
        //     variantId = currentVariantId;
        //     console.log("Length 1 → using Shopify variant ID for position 0");
        //   } else if (data.data.length === 2 && index === 1) {
        //     variantId = currentVariantId;
        //     console.log("Length 2 → using Shopify variant ID for position 1");
        //   }
        // }

        const lineItem = {
          id: variantId,
          quantity: 1
        };

        // Add properties only if customAttributes is valid
        if (
          item.customAttributes &&
          Array.isArray(item.customAttributes) &&
          item.customAttributes.length > 0
        ) {
          lineItem.properties = {};
          const personalisationKeys = [];

          item.customAttributes.forEach(attr => {
            if (!attr.key || attr.value == null) return;

            let key = attr.key;

            if (key === 'Title') {
              // Parent-product tracker: used by our own cart pairing/removal-sync
              // (see custom-scripts.js) AND deliberately left visible on Checkout/
              // order-status/emails so staff and the customer can see which product
              // this "Service · Initialling" line belongs to — Shopify showed this by
              // default before, don't hide it.
              key = 'Product';
            } else if (PERSONALISATION_KEY_LABELS.hasOwnProperty(key)) {
              key = PERSONALISATION_KEY_LABELS[key];
              personalisationKeys.push(key);
            } else if (PERSONALISATION_PREFIX.test(key)) {
              // Fallback for any Personalisation_* key Threedium sends that we
              // haven't explicitly labelled above — still strip the prefix so it
              // doesn't show up raw on Checkout.
              key = key.replace(PERSONALISATION_PREFIX, '').replace(/_/g, ' ').trim();
              personalisationKeys.push(key);
            }

            lineItem.properties[key] = String(attr.value);
          });

          if (personalisationKeys.length) {
            // Hidden marker (see custom-scripts.js) so removal-sync can identify
            // exactly which properties on this line are personalisation-related,
            // now that they no longer share a common "Personalisation_" prefix.
            lineItem.properties._personalisation_keys = personalisationKeys.join(',');
          }
        }

        return lineItem;
      });

      // Reject the whole batch if any personalisation property is blank/whitespace
      // (Threedium's own iframe UI doesn't trim, so " " passes its required-field
      // check but shouldn't reach the cart), or if the Initials property itself
      // contains anything other than letters/numbers/dots. Dots are intentional,
      // not a stray symbol — Threedium's "Add dots to initials" option formats
      // initials as e.g. "J.D.S." instead of "JDS", and that's legitimate input,
      // not something to block.
      const ALPHANUMERIC_PATTERN = /^[A-Za-z0-9.]+$/;

      let blockReason = null;
      items.some((item) => {
        if (!item.properties) return false;
        return Object.keys(item.properties).some((key) => {
          const value = item.properties[key];
          if (typeof value !== 'string') return false;
          const trimmed = value.trim();

          if (trimmed === '') {
            blockReason = 'blank';
            return true;
          }
          if (key === 'Initials' && !ALPHANUMERIC_PATTERN.test(trimmed)) {
            blockReason = 'invalid';
            return true;
          }
          return false;
        });
      });

      if (blockReason) {
        const popup = findThrediumContext();
        const messages = {
          blank: (popup && popup.dataset.blankInitialsError) ||
            'Please enter your initials before adding this to your bag.',
          invalid: (popup && popup.dataset.invalidInitialsError) ||
            'Only letters and numbers are allowed for initials.',
        };
        console.warn('[ATC3dium] Blocked add-to-cart:', blockReason, items);
        showThrediumPopupToast(popup, messages[blockReason]);
        resetThrediumIframe(popup);
        return;
      }

      // {% comment %} // Safety check: ensure all IDs look valid
      // if (items.some(item => !Number.isInteger(item.id) || item.id <= 0)) {
      //   console.error("Invalid variant ID after mapping:", items);
      //   alert("Configuration error: Invalid product variant ID.");
      //   return;
      // } {% endcomment %}

      // When the iframe sends multiple items in one event (customised product +
      // initialling tracker), tag both with a shared hidden _pairing_id and an
      // explicit _pairing_role so the cart drawer's remove handler can keep the
      // bundle consistent. Role detection: the product has many customisation
      // attributes (Body, Frame, Hardware, Trolley, etc.); the initialling
      // tracker has none of those (it's just a £0 service marker).
      if (items.length > 1) {
        const pairingId = `pair_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        const productAttrPattern = /^(Body|Frame|Hardware|Leathers?|Straps?|Guides?|Corners?|Handles?|Wheels|Trolley|Lining|Webbing)/i;

        items.forEach((it, idx) => {
          it.properties = it.properties || {};
          it.properties._pairing_id = pairingId;

          // Decide role from the original Threedium customAttributes for this index.
          const sourceAttrs = (data.data[idx] && data.data[idx].customAttributes) || [];
          const hasProductAttrs = sourceAttrs.some((a) => a && a.key && productAttrPattern.test(a.key));
          it.properties._pairing_role = hasProductAttrs ? 'product' : 'initialling';
        });
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      })
      .then(async response => {
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          console.error("Shopify rejected:", errData, "Status:", response.status);
          throw new Error(JSON.stringify(errData) || 'Cart add failed');
        }
        return response.json();
      })
      .then(result => {

        // {% comment %} HIDE POPUP {% endcomment %}
         document.querySelectorAll('.threedium-popup').forEach(el => {
          if (el.classList.contains('active')) {
            el.classList.remove('active');
            document.body.style.overflow = '';
          }
        });
        // {% comment %} HIDE POPUP {% endcomment %}

        document.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } })
        );

        setTimeout(() => {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && drawer.show) drawer.show(null, true);
        }, 400);

        fetch('/cart.js')
          .then(r => r.json())
          .then(cart => {
            if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.publish) {
              window.FoxTheme.pubsub.publish(
                window.FoxTheme.pubsub.PUB_SUB_EVENTS && window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
                { cart }
              );
            }
          });
      })
      .catch(error => {
        console.error('Add failed:', error);
        // {% comment %} alert('Add to cart failed: ' + (error.message || 'Unknown error')); {% endcomment %}
      });
    } else {
      console.warn('Invalid or empty add-to-cart data:', data);
    }
  });